import { findRegimen, type Meal } from "@/data/meal-plan";
import type { ChoiceGroupSelections, DayOfWeek, DayPlan, MealSelections, WeekPlan } from "@/types/meal-plan-state";
import { buildMealSelectionsFromNote, menuForNote } from "./ingredient-mapping";
import { createMealSelections, getMealChoices, getMealMenu, setDayMealMenu } from "./random-plan";

/**
 * "Copiar mis comidas": the planner's lunch and dinner notes of a week,
 * copied into the same days of the friend's (the owner's) week. Each note
 * fills the owner's own meal, so portions are the owner's.
 */

export type CopyMealName = "lunch" | "dinner";

export interface CopyCandidate {
  day: DayOfWeek;
  meal: CopyMealName;
  /** The planner's note for this meal. */
  note: string;
  /** The owner's meal to fill; null when the owner's day has no regimen or no such meal. */
  targetMealIndex: number | null;
  /**
   * What the copy would overwrite, when the owner's meal isn't empty: its
   * note, else its menu and foods (or "lo planificado" for skips only).
   */
  replaces: string | null;
  /** Checked by default: true iff the owner's meal is empty. Overwriting is never the default. */
  defaultChecked: boolean;
}

const WEEK_DAYS: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const COPY_MEALS: CopyMealName[] = ["lunch", "dinner"];

/** Index of `meal` in the day's regimen, when the day has that meal and meals to fill. */
function mealIndexOf(day: DayPlan, meal: CopyMealName): number | null {
  const regimen = findRegimen(day.regimenId);
  if (!regimen || !day.meals) return null;
  const index = regimen.meals.findIndex((m) => m.name === meal);
  return index === -1 || !day.meals[index] ? null : index;
}

function noteAt(day: DayPlan, index: number): string {
  return (day.mealNotes?.[index] ?? "").trim();
}

function sameGroup(a: ChoiceGroupSelections | undefined, b: ChoiceGroupSelections | undefined): boolean {
  if (!a || !b) return a === b;
  if (Boolean(a.skipped) !== Boolean(b.skipped)) return false;
  const key = (s: { optionId: string; count: number }) => `${s.optionId}:${s.count}`;
  const keysA = a.selections.filter((s) => s.count > 0).map(key).sort();
  const keysB = b.selections.filter((s) => s.count > 0).map(key).sort();
  return keysA.length === keysB.length && keysA.every((k, i) => k === keysB[i]);
}

/**
 * Empty: no note, no menu chosen, and selections equal to a fresh meal's
 * ("comido" ticks don't count). "Has selections" isn't the test: a menu meal
 * gets its fixed foods pre-selected as soon as its menu is chosen. Choosing
 * the menu is planning, though, so such a meal isn't empty even with nothing
 * else picked.
 */
export function isMealEmpty(day: DayPlan, meal: Meal, index: number): boolean {
  if (noteAt(day, index)) return false;
  if (day.mealMenus?.[index] !== undefined) return false;
  const fresh = createMealSelections(meal);
  const current: MealSelections = day.meals?.[index] ?? [];
  return current.length === fresh.length && current.every((cg, i) => sameGroup(cg, fresh[i]));
}

/** A short description of a non-empty meal without a note: its menu and foods. */
function describeMeal(day: DayPlan, meal: Meal, index: number): string {
  const menuId = day.mealMenus?.[index];
  const choices = getMealChoices(meal, menuId);
  const sels = day.meals?.[index] ?? [];
  const foods: string[] = [];
  choices.forEach((group, ci) => {
    const cg = sels[ci];
    if (!cg || cg.skipped) return;
    for (const s of cg.selections) {
      const option = group.options.find((o) => o.id === s.optionId);
      if (option && s.count > 0) foods.push(s.count > 1 ? `${option.name}×${s.count}` : option.name);
    }
  });
  const text = [getMealMenu(meal, menuId)?.name, foods.join(", ")].filter(Boolean).join(" · ");
  return text || "lo planificado";
}

/**
 * One candidate per lunch or dinner the planner (`source`) wrote a note for,
 * in week order, lunch before dinner, placed on the owner's (`target`) meal
 * of the same name.
 */
export function planCopyMealNotes(source: WeekPlan, target: WeekPlan): CopyCandidate[] {
  const candidates: CopyCandidate[] = [];
  for (const day of WEEK_DAYS) {
    for (const meal of COPY_MEALS) {
      const sourceIndex = mealIndexOf(source[day], meal);
      const note = sourceIndex === null ? "" : noteAt(source[day], sourceIndex);
      if (!note) continue;
      const targetDay = target[day];
      const targetMealIndex = mealIndexOf(targetDay, meal);
      if (targetMealIndex === null) {
        candidates.push({ day, meal, note, targetMealIndex, replaces: null, defaultChecked: false });
        continue;
      }
      const targetMeal = findRegimen(targetDay.regimenId)!.meals[targetMealIndex]!;
      const empty = isMealEmpty(targetDay, targetMeal, targetMealIndex);
      candidates.push({
        day,
        meal,
        note,
        targetMealIndex,
        replaces: empty ? null : noteAt(targetDay, targetMealIndex) || describeMeal(targetDay, targetMeal, targetMealIndex),
        defaultChecked: empty,
      });
    }
  }
  return candidates;
}

/**
 * True when the copy set anything beyond the note: a newly chosen menu, or
 * any food (vegetable and fat defaults included) a fresh meal of that menu
 * doesn't already have.
 */
function noteFilledSomething(meal: Meal, menuBefore: string | undefined, menuId: string | undefined, built: MealSelections): boolean {
  if (menuId !== menuBefore) return true;
  const fresh = createMealSelections(meal, menuId);
  return built.some((cg, i) => !sameGroup(cg, fresh[i]));
}

/**
 * Copy the picked notes into `target`, each filling the owner's meal the way
 * typing the note on Comidas would (lunch is not mirrored into dinner: dinner
 * gets the planner's own dinner note). The meal index is re-resolved against
 * `target`, so a pick whose meal no longer exists is skipped. `noteOnly`
 * lists the meals that got nothing but the note: a menu meal whose note
 * matches no menu, or a note that names nothing the meal offers in a meal
 * without default foods (a meal that got only its vegetable / fat defaults
 * did get something, so it isn't listed).
 * `copied` counts the meals written. Pure: `target` is never mutated.
 */
export function applyCopyMealNotes(
  target: WeekPlan,
  picks: CopyCandidate[],
): { plan: WeekPlan; noteOnly: { day: DayOfWeek; meal: CopyMealName }[]; copied: number } {
  let plan = target;
  let copied = 0;
  const noteOnly: { day: DayOfWeek; meal: CopyMealName }[] = [];
  for (const pick of picks) {
    let day = plan[pick.day];
    const index = mealIndexOf(day, pick.meal);
    const regimen = findRegimen(day.regimenId);
    if (index === null || !regimen) continue;
    const meal = regimen.meals[index]!;
    const note = pick.note.trim();
    if (!note) continue;

    // As typing it on Comidas: a menu the owner's old note picked follows the new note.
    const menuId = menuForNote(note, meal, day.mealMenus?.[index], day.mealNotes?.[index] ?? "");
    day = { ...day, mealNotes: { ...day.mealNotes, [index]: note } };
    const menuBefore = day.mealMenus?.[index];
    if (menuId === null) {
      // A menu meal with nothing to go on: just the note, as on Comidas.
      noteOnly.push({ day: pick.day, meal: pick.meal });
    } else {
      if (menuId !== undefined && menuId !== day.mealMenus?.[index]) {
        day = setDayMealMenu(day, meal, index, menuId);
      }
      const built = buildMealSelectionsFromNote(note, meal, menuId);
      day = { ...day, meals: day.meals!.map((m, i) => (i === index ? built : m)) };
      if (!noteFilledSomething(meal, menuBefore, menuId, built)) noteOnly.push({ day: pick.day, meal: pick.meal });
    }
    plan = { ...plan, [pick.day]: day };
    copied += 1;
  }
  return { plan, noteOnly, copied };
}
